/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { useCallback } from 'react';

import { FeelPlaygroundCollapsiblePanel } from 'features/properties-panel-feel-playground/playground-panel';
import { Status } from 'features/properties-panel-feel-playground/status';
import { getTooltipMessage, ValidationStatus } from 'features/properties-panel-feel-playground/utils/helpers';

import { JsonEditor } from './JsonEditor';

/** @typedef {import('../status/Status').StatusType} StatusType */

/**
 * @typedef {Object} ValidationState
 * @property {StatusType} status
 * @property {string} [message]
 */

/**
 * @param {Object} props
 * @property {string} props.value
 * @property {(value: string) => void} props.onChange
 * @property {string} [props.placeholder]
 * @property {string} expandedHeight
 * @property {ValidationState} props.validation
 * @property {(validation: ValidationState) => void} props.onValidationChange
 * @property {boolean} props.isCollapsed
 * @property {() => void} props.onToggleCollapsed
 */

export const JsonEditorPanel = ({
  value,
  onChange,
  placeholder,
  expandedHeight,
  validation,
  onValidationChange,
  isCollapsed,
  onToggleCollapsed
}) => {
  const handleInput = useCallback(
    (newValue) => {
      onChange(newValue);
      onValidationChange({
        status: ValidationStatus.LOADING
      });
    },
    [onChange, onValidationChange]
  );

  const handleLint = useCallback(
    (diagnostics) => {
      if (diagnostics.length === 0) {
        onValidationChange({ status: ValidationStatus.SUCCESS, message: 'Valid JSON' });
      } else {
        onValidationChange({
          status: ValidationStatus.ERROR,
          message: diagnostics[0].message
        });
      }
    },
    [onValidationChange]
  );

  return (
    <FeelPlaygroundCollapsiblePanel
      title="Context"
      expandedHeight={expandedHeight}
      isCollapsed={isCollapsed}
      onToggleCollapsed={onToggleCollapsed}
      headerItems={
        value && (
          <Status
            status={validation.status}
            tooltipMessage={getTooltipMessage('json', validation.status, validation.message)}
          />
        )
      }
    >
      <JsonEditor value={value} onChange={handleInput} placeholderText={placeholder} onLint={handleLint} />
    </FeelPlaygroundCollapsiblePanel>
  );
};
